import { Injectable, OnDestroy } from '@angular/core';
import { orderBy, where, limit, Unsubscribe } from 'firebase/firestore';
import { BehaviorSubject } from 'rxjs';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { UserService } from 'src/app/services/user/user.service';
import { UserNotification } from 'src/app/services/messaging/models';

@Injectable({
  providedIn: 'root'
})
export class NotificationCenterService implements OnDestroy {

  public notifications$ = new BehaviorSubject<UserNotification[]>([]);
  public unreadCount$ = new BehaviorSubject<number>(0);

  private listener: Unsubscribe;
  private listening = false;

  constructor(
    private firebase: FirebaseService,
    private user: UserService,
  ) {}

  ngOnDestroy(): void {
    this.stopListening();
  }

  public startListening(): void {
    if (this.listening) {
      return;
    }
    if (!this.user.user?.uid) {
      return;
    }

    this.listening = true;
    this.listener = this.firebase.firestore.collectionListen(
      `users/${this.user.user.uid}/notifications`,
      [
        orderBy('createdOn', 'desc'),
        limit(30),
      ],
      (snapshots) => {
        const notifications: UserNotification[] = snapshots.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: data.type,
            conversationId: data.conversationId,
            messageId: data.messageId,
            threadId: data.threadId || undefined,
            senderId: data.senderId,
            senderName: data.senderName,
            preview: data.preview,
            read: data.read || false,
            createdOn: data.createdOn ? this.firebase.firestore.timestampToDate(data.createdOn).getTime() : 0,
          };
        });

        this.notifications$.next(notifications);
        this.unreadCount$.next(notifications.filter(n => !n.read).length);
      },
    );
  }

  public stopListening(): void {
    if (this.listener) {
      this.listener();
      this.listener = null;
    }
    this.listening = false;
  }

  public async markAsRead(notificationId: string): Promise<void> {
    if (!this.user.user?.uid) {
      return;
    }

    await this.firebase.firestore.docUpdate(
      `users/${this.user.user.uid}/notifications/${notificationId}`,
      { read: true },
    );
  }

  public async markAllAsRead(): Promise<void> {
    if (!this.user.user?.uid) {
      return;
    }

    const unread = this.notifications$.getValue().filter(n => !n.read);
    if (unread.length === 0) {
      return;
    }

    const basePath = `users/${this.user.user.uid}/notifications`;
    await this.firebase.firestore.batchUpdate(
      unread.map(n => ({ path: `${basePath}/${n.id}`, data: { read: true } })),
    );
  }
}
